import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { UtensilsCrossed, Calendar, MessageCircle, Heart, Hotel, Landmark } from 'lucide-react';

const HomePage = () => {
  const { t, language } = useLanguage();

  const cards = [
    {
      path: '/kosher-food',
      testId: 'bento-kosher-food',
      image: 'https://images.unsplash.com/photo-1748540459503-19efc015143b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxrb3NoZXIlMjBmb29kJTIwdGFibGUlMjBmZWFzdCUyMG1pZGRsZSUyMGVhc3Rlcm58ZW58MHx8fHwxNzc2NjU4NjExfDA&ixlib=rb-4.1.0&q=85',
      icon: UtensilsCrossed,
      iconBg: '#B85042',
      title: t('kosherFoodTitle'),
      desc: t('kosherFoodDesc'),
    },
    {
      path: '/shabbat',
      testId: 'bento-shabbat',
      image: 'https://customer-assets.emergentagent.com/job_jabad-gdl/artifacts/nr8klcj9_Image%203.jpeg',
      icon: Calendar,
      iconBg: '#D4AF37',
      title: t('shabbatTitle'),
      desc: t('shabbatDesc'),
    },
    {
      path: 'https://www.jabadmx.com/templates/donate_cdo/aid/4970020/jewish/Donate.htm',
      external: true,
      testId: 'bento-donations',
      image: 'https://images.pexels.com/photos/35237380/pexels-photo-35237380.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      icon: Heart,
      iconBg: '#D4AF37',
      title: t('donationsTitle'),
      desc: t('donationsDesc'),
    },
    {
      path: '/hotels',
      testId: 'bento-hotels',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      icon: Hotel,
      iconBg: '#B85042',
      title: t('hotelsTitle'),
      desc: t('hotelsDesc'),
    },
    {
      path: '/attractions',
      testId: 'bento-attractions',
      image: 'https://images.unsplash.com/photo-1694923963403-2443a0215818?w=1200&q=75&auto=format&fit=crop',
      icon: Landmark,
      iconBg: '#0F2922',
      title: t('attractionsTitle'),
      desc: t('attractionsDesc'),
    },
    {
      path: '/contact',
      testId: 'bento-contact',
      image: 'https://customer-assets.emergentagent.com/job_jabad-gdl/artifacts/ruugius0_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2001_03_33%20AM.png',
      imagePos: '50% 20%',
      icon: MessageCircle,
      iconBg: '#25D366',
      title: t('contactTitle'),
      desc: t('contactDesc'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Hero Section */}
      <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_jabad-gdl/artifacts/ev58dquz_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2001_18_03%20AM.png)',
            backgroundPosition: '30% 65%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2922]/40 to-[#0F2922]/30" />
        </div>
        <div className="relative z-10 text-center px-4 py-20">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="hero-title">
            {t('welcome')}
          </h1>
          <p className="font-body text-lg sm:text-xl text-white/90 max-w-2xl mx-auto" data-testid="hero-subtitle">
            {t('welcomeSubtitle')}
          </p>
        </div>
      </section>

      {/* Cards Grid - 2x3 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-16 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, idx) => {
            const isLastAlone = idx === cards.length - 1 && cards.length % 3 !== 0;
            const CardWrapper = card.external 
              ? ({ children, ...props }) => <a href={card.path} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
              : ({ children, ...props }) => <Link to={card.path} {...props}>{children}</Link>;
            
            return (
              <CardWrapper key={card.testId} className={`group ${isLastAlone ? 'sm:col-span-2 lg:col-span-3' : ''}`} data-testid={card.testId}>
                <div className={`relative ${isLastAlone ? 'h-[200px]' : 'h-[260px]'} rounded-2xl overflow-hidden bg-white border border-[#E5E3D8] hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}>
                  <div 
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                    style={{
                      backgroundImage: `url(${card.image})`,
                      backgroundPosition: card.imagePos || 'center',
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2922]/85 via-[#0F2922]/30 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: card.iconBg }}>
                        <card.icon className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">
                        {card.title}
                      </h2>
                    </div>
                    <p className="font-body text-white/80 text-sm">
                      {card.desc}
                    </p>
                  </div>
                </div>
              </CardWrapper>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F2922] text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-heading text-xl font-bold mb-2">Jabad Guadalajara Copa Mundial 2026</p>
          <p className="font-body text-white/70 text-sm">
            &copy; {new Date().getFullYear()} Jabad Guadalajara. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
